import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";

type FaqItem = { q: string; a: ReactNode; aText: string };
type FaqGroup = { title: string; items: FaqItem[] };

const mail = (
  <a
    href="mailto:hello@houseofapocrypha.com"
    className="text-gold underline-offset-4 hover:underline"
  >
    hello@houseofapocrypha.com
  </a>
);

const releaseList = (
  <Link
    to="/"
    hash="release-access"
    className="text-gold underline-offset-4 hover:underline"
  >
    Release List
  </Link>
);

const groups: FaqGroup[] = [
  {
    title: "Craft & Philosophy",
    items: [
      {
        q: "How often do you release new perfumes?",
        aText:
          "House of Apocrypha releases perfumes in seasonal Chapters — four times a year, opening in the third week of September, December, March, and June. Each Chapter is produced in a small, limited run based on pre-orders. Because we work with natural materials — resins, absolutes, tinctures, flowers, woods, and botanicals — each batch carries subtle variations from harvest, extraction, aging, and the perfumer's hand. When a Chapter closes, the bottles from that release are no longer available for pre-order. Some fragrances may return in a future Chapter, but they are unlikely to be an exact replica.",
        a: (
          <>
            <p>
              House of Apocrypha releases perfumes in seasonal Chapters — four
              times a year, opening in the third week of September, December,
              March, and June.
            </p>
            <p className="mt-3">
              Each Chapter is produced in a small, limited run based on
              pre-orders. Because we work with natural materials — resins,
              absolutes, tinctures, flowers, woods, and botanicals — each batch
              carries subtle variations from harvest, extraction, aging, and
              the perfumer's hand.
            </p>
            <p className="mt-3">
              When a Chapter closes, the bottles from that release are no
              longer available for pre-order. Some fragrances may return in a
              future Chapter, but they are unlikely to be an exact replica.
            </p>
          </>
        ),
      },
      {
        q: "What is a Chapter?",
        aText:
          "A Chapter is a seasonal release of fragrances from House of Apocrypha. Each Chapter has its own atmosphere, materials, and limited production window. Bottles are made in small batches and offered by pre-order while the Chapter is open.",
        a: (
          <>
            <p>A Chapter is a seasonal release of fragrances from House of Apocrypha.</p>
            <p className="mt-3">
              Each Chapter has its own atmosphere, materials, and limited
              production window. Bottles are made in small batches and offered
              by pre-order while the Chapter is open.
            </p>
          </>
        ),
      },
      {
        q: "Will a sold-out fragrance ever return?",
        aText:
          "Sometimes. A fragrance may return in a later Chapter, but natural materials are never perfectly identical from year to year. Harvests shift, tinctures age, resins vary, and formulas may evolve. Think of each release as a new printing of an old text: familiar, but never exactly the same.",
        a: (
          <>
            <p>Sometimes.</p>
            <p className="mt-3">
              A fragrance may return in a later Chapter, but natural materials
              are never perfectly identical from year to year. Harvests shift,
              tinctures age, resins vary, and formulas may evolve. Think of
              each release as a new printing of an old text: familiar, but
              never exactly the same.
            </p>
          </>
        ),
      },
      {
        q: "Are your perfumes gendered?",
        aText: "No. Scent is a sense, not a segment. Wear what moves you.",
        a: (
          <>
            <p>No.</p>
            <p className="mt-3">Scent is a sense, not a segment. Wear what moves you.</p>
          </>
        ),
      },
      {
        q: "Are your perfumes 100% natural?",
        aText:
          "Yes. Our perfumes are made with natural aromatic materials, natural isolates, tinctures, absolutes, balsams and aromatic plant gums, botanicals, and cane alcohol. Natural isolates are single aromatic molecules derived from plant materials through processes such as distillation or fractionation. They allow us to build more precise natural compositions without using petrochemical aroma synthetics.",
        a: (
          <>
            <p>
              Yes. Our perfumes are made with natural aromatic materials,
              natural isolates, tinctures, absolutes, balsams and aromatic
              plant gums, botanicals, and cane alcohol.
            </p>
            <p className="mt-3">
              Natural isolates are single aromatic molecules derived from plant
              materials through processes such as distillation or
              fractionation. They allow us to build more precise natural
              compositions without using petrochemical aroma synthetics.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "Product & Ingredients",
    items: [
      {
        q: "What concentration are your perfumes?",
        aText:
          "Our full-size perfumes are Eau de Parfum strength, blended at approximately 20–25% aromatic concentration. This gives them richness and depth. While some natural perfumes wear closer to the skin, this house often creates bold compositions that carry well and can last for many hours, sometimes exceeding 8 hours with noticeable projection. As always, performance can vary from person to person.",
        a: (
          <>
            <p>
              Our full-size perfumes are Eau de Parfum strength, blended at
              approximately 20–25% aromatic concentration.
            </p>
            <p className="mt-3">
              This gives them richness and depth. While some natural perfumes
              wear closer to the skin, this house often creates bold
              compositions that carry well and can last for many hours,
              sometimes exceeding 8 hours with noticeable projection. As
              always, performance can vary from person to person.
            </p>
          </>
        ),
      },
      {
        q: "How long do natural perfumes last?",
        aText:
          "Natural perfumes tend to stay more intimate than conventional synthetic perfumes. Resinous, woody, smoky, balsamic, or ambered compositions may last 6–12 hours or longer. Lighter florals, citruses, green notes, and airy accords may last closer to 3–5 hours. Fabric can hold scent longer than skin. Longevity varies with skin chemistry, weather, application, and the materials in the formula.",
        a: (
          <>
            <p>
              Natural perfumes tend to stay more intimate than conventional
              synthetic perfumes.
            </p>
            <p className="mt-3">As a general guide:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>
                Resinous, woody, smoky, balsamic, or ambered compositions may
                last 6–12 hours or longer.
              </li>
              <li>
                Lighter florals, citruses, green notes, and airy accords may
                last closer to 3–5 hours.
              </li>
              <li>Fabric can hold scent longer than skin.</li>
            </ul>
            <p className="mt-3">
              Longevity varies with skin chemistry, weather, application, and
              the materials in the formula.
            </p>
          </>
        ),
      },
      {
        q: "How should I apply natural perfume?",
        aText:
          "Apply to pulse points, hair ends, clothing, or fabric accessories. For the longest wear, apply to moisturized skin or to a scarf, collar, or sleeve. Natural perfumes often unfold slowly, so give them time before deciding how you feel about them.",
        a: (
          <>
            <p>Apply to pulse points, hair ends, clothing, or fabric accessories.</p>
            <p className="mt-3">
              For the longest wear, apply to moisturized skin or to a scarf,
              collar, or sleeve. Natural perfumes often unfold slowly, so give
              them time before deciding how you feel about them.
            </p>
          </>
        ),
      },
      {
        q: "Can I spray perfume on fabric?",
        aText:
          "Yes, but test first. Natural materials can vary in color and density. Resins (plant gum aromatics), absolutes, and darker botanical extracts may stain delicate or pale fabrics. We recommend testing on an inconspicuous area first.",
        a: (
          <>
            <p>Yes, but test first.</p>
            <p className="mt-3">
              Natural materials can vary in color and density. Resins (plant
              gum aromatics), absolutes, and darker botanical extracts may
              stain delicate or pale fabrics. We recommend testing on an
              inconspicuous area first.
            </p>
          </>
        ),
      },
      {
        q: "How should I store my perfume?",
        aText:
          "Keep your bottle away from heat, direct sunlight, and humidity. A cool, dark place is best. Natural materials are more alive and less standardized than synthetic perfume materials, so careful storage helps preserve the fragrance over time. They will also continue to evolve and grow in depth as they are stored properly.",
        a: (
          <>
            <p>Keep your bottle away from heat, direct sunlight, and humidity.</p>
            <p className="mt-3">
              A cool, dark place is best. Natural materials are more alive and
              less standardized than synthetic perfume materials, so careful
              storage helps preserve the fragrance over time. They will also
              continue to evolve and grow in depth as they are stored properly.
            </p>
          </>
        ),
      },
      {
        q: "Are your perfumes vegan?",
        aText:
          "Not always. Many of our formulas are entirely plant-based, but some may include traditional animal-derived materials such as honey absolute, wool absolute, seashell tincture, or natural ambergris. When a fragrance contains an animal-derived material, we aim to make that clear on the product page.",
        a: (
          <>
            <p>Not always.</p>
            <p className="mt-3">
              Many of our formulas are entirely plant-based, but some may
              include traditional animal-derived materials such as honey
              absolute, wool absolute, seashell tincture, or natural ambergris.
              When a fragrance contains an animal-derived material, we aim to
              make that clear on the product page.
            </p>
          </>
        ),
      },
      {
        q: "Are your ingredients ethically sourced?",
        aText:
          "We source as responsibly as a micro-studio can. We look for suppliers who prioritize sound ecological practices, traceability, and fair labor. We avoid threatened botanicals unless we have confidence in the sourcing. Because harvests, suppliers, and material availability change over time, we review our materials each season and adjust when needed. Transparency is ongoing rather than absolute, but ethical sourcing guides every purchase we make.",
        a: (
          <>
            <p>We source as responsibly as a micro-studio can.</p>
            <p className="mt-3">
              We look for suppliers who prioritize sound ecological practices,
              traceability, and fair labor. We avoid threatened botanicals
              unless we have confidence in the sourcing. Because harvests,
              suppliers, and material availability change over time, we review
              our materials each season and adjust when needed.
            </p>
            <p className="mt-3">
              Transparency is ongoing rather than absolute, but ethical
              sourcing guides every purchase we make.
            </p>
          </>
        ),
      },
      {
        q: "What sizes do you offer?",
        aText: "Full bottles are 30ml, priced at $135 each.",
        a: <p>Full bottles are 30ml, priced at $135 each.</p>,
      },
    ],
  },
  {
    title: "Orders & Chapters",
    items: [
      {
        q: "When does the next Chapter open?",
        aText:
          "Chapters open seasonally — in the third week of September, December, March, and June. Join the Release List for early access before the public window opens.",
        a: (
          <>
            <p>
              Chapters open seasonally — in the third week of September,
              December, March, and June.
            </p>
            <p className="mt-3">
              Join the {releaseList} for early access before the public window
              opens.
            </p>
          </>
        ),
      },
      {
        q: "How does pre-ordering work?",
        aText:
          "When a Chapter opens, full bottles are available by pre-order. Choose your fragrance and place your order during the open window. Payment is taken at checkout. Bottles are then blended, finished, packed, and shipped after the Chapter closes. The estimated dispatch window is shown on each product page.",
        a: (
          <>
            <p>When a Chapter opens, full bottles are available by pre-order.</p>
            <p className="mt-3">
              Choose your fragrance and place your order during the open
              window. Payment is taken at checkout. Bottles are then blended,
              finished, packed, and shipped after the Chapter closes.
            </p>
            <p className="mt-3">
              The estimated dispatch window is shown on each product page.
            </p>
          </>
        ),
      },
      {
        q: "When will my bottle ship?",
        aText:
          "Each product page shows an estimated dispatch window. Orders from the same Chapter ship together once production is complete.",
        a: (
          <>
            <p>Each product page shows an estimated dispatch window.</p>
            <p className="mt-3">
              Orders from the same Chapter ship together once production is
              complete.
            </p>
          </>
        ),
      },
      {
        q: "What happens when a Chapter closes?",
        aText:
          "Full bottle pre-orders close. Chapter Vials are no longer available. The release moves into the Library, and production begins for the bottles ordered during that window.",
        a: (
          <p>
            Full bottle pre-orders close. Chapter Vials are no longer
            available. The release moves into the Library, and production
            begins for the bottles ordered during that window.
          </p>
        ),
      },
      {
        q: "What if I miss a Chapter?",
        aText:
          "When a Chapter closes, it closes. Some fragrances may return in future Chapters. Others may remain in the archive. Join the Release List to be notified when a new Chapter opens.",
        a: (
          <>
            <p>When a Chapter closes, it closes.</p>
            <p className="mt-3">
              Some fragrances may return in future Chapters. Others may remain
              in the archive. Join the {releaseList} to be notified when a new
              Chapter opens.
            </p>
          </>
        ),
      },
      {
        q: "Can I buy more than one bottle of the same fragrance?",
        aText:
          "At launch, each customer may purchase one bottle per fragrance per Chapter. You may order multiple different fragrances in the same order.",
        a: (
          <>
            <p>
              At launch, each customer may purchase one bottle per fragrance
              per Chapter.
            </p>
            <p className="mt-3">
              You may order multiple different fragrances in the same order.
            </p>
          </>
        ),
      },
      {
        q: "Why do you do it this way?",
        aText:
          "As a perfumer, I need to be able to move on. Many of the fragrances in a given Chapter have been with me for years — through iterations, reformulations, quiet revisions that never saw daylight. By the time something reaches you, I have already spent a long time with it. The Chapter model is honest about that process. A fragrance isn't permanent. It's made in a particular moment, with particular materials, by a particular hand. When it's finished, it's finished. I may return to a scent and find something new in it — but I'm not interested in making the same thing indefinitely. — Valerie, founder",
        a: (
          <>
            <p>As a perfumer, I need to be able to move on.</p>
            <p className="mt-3">
              Many of the fragrances in a given Chapter have been with me for
              years — through iterations, reformulations, quiet revisions that
              never saw daylight. By the time something reaches you, I have
              already spent a long time with it.
            </p>
            <p className="mt-3">
              The Chapter model is honest about that process. A fragrance isn't
              permanent. It's made in a particular moment, with particular
              materials, by a particular hand. When it's finished, it's
              finished. I may return to a scent and find something new in it —
              but I'm not interested in making the same thing indefinitely.
            </p>
            <p className="mt-3 font-serif italic text-parchment/90">
              — Valerie, founder
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "Chapter Vials",
    items: [
      {
        q: "What are Chapter Vials?",
        aText:
          "Chapter Vials are samples from the current seasonal Chapter, chosen individually from the fragrances currently available. They are priced at $10, $28, or $46, depending on how many you'd like to try, and are available only while the Chapter window is open. Due to limited supply, vials are not expected to remain in stock for the entire season.",
        a: (
          <>
            <p>
              Chapter Vials are samples from the current seasonal Chapter,
              chosen individually from the fragrances currently available.
            </p>
            <p className="mt-3">
              They are priced at $10, $28, or $46, depending on how many you'd
              like to try, and are available only while the Chapter window is
              open. Due to limited supply, vials are not expected to remain in
              stock for the entire season.
            </p>
          </>
        ),
      },
      {
        q: "Are Chapter Vials a Discovery Set?",
        aText:
          "No. House of Apocrypha does not sell a fixed Discovery Set. Chapter Vials are chosen individually from the fragrances in the current Chapter.",
        a: (
          <>
            <p>No.</p>
            <p className="mt-3">
              House of Apocrypha does not sell a fixed Discovery Set. Chapter
              Vials are chosen individually from the fragrances in the current
              Chapter.
            </p>
          </>
        ),
      },
      {
        q: "When do Chapter Vials ship?",
        aText:
          "Chapter Vials ship every Friday while the Chapter is open. Orders placed after Wednesday may ship the following Friday.",
        a: (
          <>
            <p>Chapter Vials ship every Friday while the Chapter is open.</p>
            <p className="mt-3">
              Orders placed after Wednesday may ship the following Friday.
            </p>
          </>
        ),
      },
      {
        q: "Can I buy Chapter Vials after the Chapter closes?",
        aText:
          "No. Chapter Vials are available only while the Chapter is open. When the Chapter closes, the vials close with it.",
        a: (
          <>
            <p>No.</p>
            <p className="mt-3">
              Chapter Vials are available only while the Chapter is open. When
              the Chapter closes, the vials close with it.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "The Vial Promise",
    items: [
      {
        q: "What is The Vial Promise?",
        aText:
          "Every full bottle includes a companion vial of the same fragrance. Wear the vial first — if the fragrance isn't a fit, you may request a return without ever opening the full bottle. Wear it for at least three days, at different times of day. Natural perfumes open slowly. The first wearing is rarely the full story.",
        a: (
          <>
            <p>
              Every full bottle includes a companion vial of the same
              fragrance. Wear the vial first — if the fragrance isn't a fit,
              you may request a return without ever opening the full bottle.
            </p>
            <p className="mt-3">
              Wear it for at least three days, at different times of day.
              Natural perfumes open slowly. The first wearing is rarely the
              full story.
            </p>
          </>
        ),
      },
      {
        q: "How do returns work?",
        aText:
          "Contact us within 14 days of delivery. To qualify for a return, the full bottle must remain unopened and sealed in its original packaging — the black outer box must be intact and the ribbon must not be cut or removed. Once your return is approved, we will send a prepaid return label. Return the unopened bottle within the following 14 days, and we will issue a refund once it is received. The companion vial remains with you.",
        a: (
          <>
            <p>Contact us within 14 days of delivery.</p>
            <p className="mt-3">
              To qualify for a return, the full bottle must remain unopened and
              sealed in its original packaging — the black outer box must be
              intact and the ribbon must not be cut or removed.
            </p>
            <p className="mt-3">
              Once your return is approved, we will send a prepaid return
              label. Return the unopened bottle within the following 14 days,
              and we will issue a refund once it is received.
            </p>
            <p className="mt-3">The companion vial remains with you.</p>
          </>
        ),
      },
      {
        q: "Can I return an opened bottle?",
        aText:
          "No. Opened bottles cannot be returned. To preserve the integrity of each small batch, full bottles must remain unopened and sealed in their original packaging.",
        a: (
          <>
            <p>No.</p>
            <p className="mt-3">
              Opened bottles cannot be returned. To preserve the integrity of
              each small batch, full bottles must remain unopened and sealed in
              their original packaging.
            </p>
          </>
        ),
      },
      {
        q: "Can I exchange for a different fragrance?",
        aText:
          "Seasonal stock cannot be guaranteed. If you would prefer store credit toward a future Chapter instead of a refund, contact us and we will arrange it when possible.",
        a: (
          <>
            <p>Seasonal stock cannot be guaranteed.</p>
            <p className="mt-3">
              If you would prefer store credit toward a future Chapter instead
              of a refund, contact us and we will arrange it when possible.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "Shipping",
    items: [
      {
        q: "Do you ship internationally?",
        aText:
          "Not yet. Our current perfumes are alcohol-based, which makes international shipping more complicated. For now, House of Apocrypha ships only within the United States. If you are outside the U.S. and interested in a future release, join the Release List to help us understand demand. We are exploring additional formats for the future.",
        a: (
          <>
            <p>Not yet.</p>
            <p className="mt-3">
              Our current perfumes are alcohol-based, which makes international
              shipping more complicated. For now, House of Apocrypha ships only
              within the United States.
            </p>
            <p className="mt-3">
              If you are outside the U.S. and interested in a future release,
              join the {releaseList} to help us understand demand. We are
              exploring additional formats for the future.
            </p>
          </>
        ),
      },
      {
        q: "How are orders shipped?",
        aText:
          "Chapter Vials ship during the open Chapter window. Full bottles ship after the Chapter closes, once production is complete. Your estimated dispatch window will be shown on the product page and in your order confirmation.",
        a: (
          <>
            <p>Chapter Vials ship during the open Chapter window.</p>
            <p className="mt-3">
              Full bottles ship after the Chapter closes, once production is
              complete. Your estimated dispatch window will be shown on the
              product page and in your order confirmation.
            </p>
          </>
        ),
      },
      {
        q: "What if my order arrives damaged?",
        aText:
          "Email hello@houseofapocrypha.com within 7 days of delivery with your order number and photos of the damaged item and packaging. We will make it right.",
        a: (
          <>
            <p>
              Email {mail} within 7 days of delivery with your order number and
              photos of the damaged item and packaging.
            </p>
            <p className="mt-3">We will make it right.</p>
          </>
        ),
      },
    ],
  },
  {
    title: "Gifts, Access & Press",
    items: [
      {
        q: "Can I include a gift note?",
        aText:
          "Yes. Add your note at checkout, and we will include a handwritten card when possible.",
        a: (
          <>
            <p>Yes.</p>
            <p className="mt-3">
              Add your note at checkout, and we will include a handwritten card
              when possible.
            </p>
          </>
        ),
      },
      {
        q: "Is this good for gifting?",
        aText:
          "Every order ships without pricing or an invoice — and the bottle arrives in its sealed black box, which is already gift-ready as it comes. Additional gift wrapping may be available for select Chapters. If offered, details will appear at checkout.",
        a: (
          <>
            <p>
              Every order ships without pricing or an invoice — and the bottle
              arrives in its sealed black box, which is already gift-ready as it
              comes.
            </p>
            <p className="mt-3">
              Additional gift wrapping may be available for select Chapters. If
              offered, details will appear at checkout.
            </p>
          </>
        ),
      },
      {
        q: "Do you have sales?",
        aText:
          "No. We do not run seasonal sales or markdowns. Chapter pricing reflects small-batch production, rare natural materials, and limited availability. We may occasionally offer early access, loyalty rewards, or special privileges to the Release List.",
        a: (
          <>
            <p>No.</p>
            <p className="mt-3">
              We do not run seasonal sales or markdowns. Chapter pricing
              reflects small-batch production, rare natural materials, and
              limited availability.
            </p>
            <p className="mt-3">
              We may occasionally offer early access, loyalty rewards, or
              special privileges to the {releaseList}.
            </p>
          </>
        ),
      },
      {
        q: "What is the Release List, and how do I join?",
        aText:
          "The Release List is how we stay in touch between Chapters. Subscribers receive early access before each Chapter opens to the public, along with occasional updates on materials, archive additions, and notes from the studio. Join the Release List.",
        a: (
          <>
            <p>The Release List is how we stay in touch between Chapters.</p>
            <p className="mt-3">
              Subscribers receive early access before each Chapter opens to the
              public, along with occasional updates on materials, archive
              additions, and notes from the studio.
            </p>
            <p className="mt-3">Join the {releaseList} →</p>
          </>
        ),
      },
      {
        q: "I'm a retailer, writer, or editor. Who do I contact?",
        aText:
          "Send a note to hello@houseofapocrypha.com with your store details, publication, or press inquiry. We love kindred spirits.",
        a: (
          <>
            <p>
              Send a note to {mail} with your store details, publication, or
              press inquiry.
            </p>
            <p className="mt-3">We love kindred spirits.</p>
          </>
        ),
      },
      {
        q: "How do I get in touch?",
        aText:
          "For questions not covered here, email hello@houseofapocrypha.com. We'll get back to you as soon as we can.",
        a: (
          <>
            <p>For questions not covered here, email {mail}.</p>
            <p className="mt-3">We'll get back to you as soon as we can.</p>
          </>
        ),
      },
    ],
  },
];

export const STATIC_FAQ_GROUPS = groups;

/** Flat list for FAQPage JSON-LD. */
export function staticFaqPairs(): { q: string; a: string }[] {
  return groups.flatMap((g) => g.items.map((i) => ({ q: i.q, a: i.aText })));
}

function FaqRow({ item }: { item: FaqItem }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 py-5 text-left"
      >
        <span className="font-serif text-lg text-parchment">{item.q}</span>
        <span
          className={`font-display text-gold transition-transform duration-300 ${
            open ? "rotate-45" : ""
          }`}
        >
          +
        </span>
      </button>
      {open && (
        <div className="pb-5 text-base leading-relaxed text-foreground/80">
          {item.a}
        </div>
      )}
    </div>
  );
}

export function StaticFaqSections() {
  return (
    <div className="mt-16 space-y-14">
      {groups.map((g) => (
        <section key={g.title}>
          <h2 className="font-display text-xs uppercase tracking-[0.24em] text-gold">
            {g.title}
          </h2>
          <div className="mt-4">
            {g.items.map((item) => (
              <FaqRow key={item.q} item={item} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
